/**
 * Form Validation Utilities
 * 
 * Centralized validation functions untuk memastikan konsistensi
 * dan menghindari duplikasi kode validasi di berbagai form
 */

import { showWarning } from "./toast";

/**
 * Validate required fields
 * @param {Object} fields - Object dengan key = field name, value = field value
 * @param {string} customMessage - Custom error message (optional)
 * @returns {boolean} True jika semua field terisi, false jika ada yang kosong
 */
export function validateRequired(fields, customMessage = "Semua kolom wajib diisi!") {
  const hasEmptyField = Object.values(fields).some(
    (value) => value === "" || value === null || value === undefined
  );

  if (hasEmptyField) {
    showWarning(customMessage);
    return false;
  }

  return true;
}

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True jika email valid
 */
export function validateEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!email) {
    showWarning("Email wajib diisi!");
    return false;
  }

  if (!emailRegex.test(email)) {
    showWarning("Email tidak valid!");
    return false;
  }

  return true;
}

/**
 * Validate password match
 * @param {string} password - Password
 * @param {string} confirmPassword - Confirm password
 * @returns {boolean} True jika password cocok
 */
export function validatePasswordMatch(password, confirmPassword) {
  if (!password || !confirmPassword) {
    showWarning("Password dan konfirmasi password wajib diisi!");
    return false;
  }

  if (password !== confirmPassword) {
    showWarning("Password tidak cocok!");
    return false;
  }

  return true;
}

/**
 * Validate minimum password length
 * @param {string} password - Password to validate
 * @param {number} minLength - Minimum length (default: 6)
 * @returns {boolean} True jika password memenuhi minimum length
 */
export function validatePasswordLength(password, minLength = 6) {
  if (!password) {
    showWarning("Password wajib diisi!");
    return false;
  }

  if (password.length < minLength) {
    showWarning(`Password minimal ${minLength} karakter!`);
    return false;
  }

  return true;
}

/**
 * Validate hex color format
 * @param {string} color - Color hex string
 * @returns {boolean} True jika format valid
 */
export function validateHexColor(color) {
  const hexRegex = /^#[0-9A-Fa-f]{6}$/;
  
  if (!color) {
    showWarning("Warna wajib dipilih!");
    return false;
  }

  if (!hexRegex.test(color)) {
    showWarning("Format warna tidak valid! Gunakan format #RRGGBB");
    return false;
  }

  return true;
}

/**
 * Validate file size
 * @param {File} file - File object
 * @param {number} maxSizeMB - Maximum size in MB (default: 5)
 * @returns {boolean} True jika ukuran file valid
 */
export function validateFileSize(file, maxSizeMB = 5) {
  if (!file) {
    showWarning("File wajib dipilih!");
    return false;
  }

  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  
  if (file.size > maxSizeBytes) {
    showWarning(`Ukuran file maksimal ${maxSizeMB}MB!`);
    return false;
  }

  return true;
}

/**
 * Validate file type
 * @param {File} file - File object
 * @param {string[]} allowedTypes - Array of allowed MIME types
 * @param {string} customMessage - Custom error message
 * @returns {boolean} True jika tipe file valid
 */
export function validateFileType(
  file,
  allowedTypes = ["image/jpeg", "image/jpg", "image/png"],
  customMessage = "Tipe file tidak valid!"
) {
  if (!file) {
    showWarning("File wajib dipilih!");
    return false;
  }

  if (!allowedTypes.includes(file.type)) {
    showWarning(customMessage);
    return false;
  }

  return true;
}

/**
 * Validate minimum array length
 * @param {Array} array - Array to validate
 * @param {number} minLength - Minimum length
 * @param {string} fieldName - Field name untuk error message
 * @returns {boolean} True jika array memenuhi minimum length
 */
export function validateMinArrayLength(array, minLength, fieldName = "Item") {
  if (!Array.isArray(array)) {
    showWarning(`${fieldName} wajib diisi!`);
    return false;
  }

  if (array.length < minLength) {
    showWarning(`Minimal ${minLength} ${fieldName.toLowerCase()} diperlukan!`);
    return false;
  }

  return true;
}
